import { z } from 'zod';
import {
  AssetItem,
  CreateAsset,
  DeleteAsset,
  DownloadRequestResponse,
  UploadRequestResponse,
  GetAsset,
  RequestAssetUpload,
  UpdateAsset,
  UploadItem,
  ListAssets,
} from '@robocorp/robocloud-asset-storage-interface';
import { externalApi } from '~/utils/api/external';
import { integrationAssetStorageApi } from '~/utils/api';
import { WithWorkspaceId } from '~/types/common';

type ListAssetsResponse = z.infer<(typeof ListAssets.http.response)[200]>;
export const listAssets = async ({ workspaceId }: WithWorkspaceId) =>
  integrationAssetStorageApi.get<ListAssetsResponse>(`/workspaces/${workspaceId}/assets`);

export type GetAssetResponse = AssetItem & DownloadRequestResponse;
export const getAsset = async ({
  workspaceId,
  assetId,
}: z.infer<typeof GetAsset.http.request.pathParams>) =>
  integrationAssetStorageApi.get<GetAssetResponse>(`/workspaces/${workspaceId}/assets/${assetId}`);

export const deleteAsset = async ({
  workspaceId,
  assetId,
}: z.infer<typeof DeleteAsset.http.request.pathParams>) =>
  integrationAssetStorageApi.delete<AssetItem>(`/workspaces/${workspaceId}/assets/${assetId}`);

export type CreateAssetInput = z.infer<(typeof CreateAsset)['http']['request']['body']>;
export type CreateAssetResponse = z.infer<(typeof CreateAsset)['http']['response']['200']>;
export const createAsset = async ({
  workspaceId,
  params,
}: { params: CreateAssetInput } & WithWorkspaceId) =>
  integrationAssetStorageApi.post<CreateAssetResponse>(`/workspaces/${workspaceId}/assets`, params);

export type UpdateAssetPath = z.infer<(typeof UpdateAsset)['http']['request']['pathParams']>;
export type UpdateAssetInput = z.infer<(typeof UpdateAsset)['http']['request']['body']> &
  UpdateAssetPath;
export type UpdateAssetResponse = z.infer<(typeof UpdateAsset)['http']['response']['200']>;
export const updateAsset = async ({ workspaceId, ...params }: UpdateAssetInput) =>
  integrationAssetStorageApi.put<UpdateAssetResponse>(
    `/workspaces/${workspaceId}/assets/${params.assetId}`,
    params,
  );

type UploadAssetInput = {
  file: File;
  presignedUrl: string;
  contentType: string;
};

export const uploadAsset = async (params: UploadAssetInput) =>
  externalApi.put(params.presignedUrl, params.file, {
    headers: {
      'Content-Type': params.contentType,
    },
  });

export const listAssetUploads = async ({
  workspaceId,
  assetId,
}: { assetId: string } & WithWorkspaceId) =>
  integrationAssetStorageApi.get<UploadItem[]>(
    `/workspaces/${workspaceId}/assets/${assetId}/uploads`,
  );

export const requestAssetDownload = async ({
  workspaceId,
  assetId,
}: { assetId: string } & WithWorkspaceId) =>
  integrationAssetStorageApi.post<DownloadRequestResponse>(
    `/workspaces/${workspaceId}/assets/${assetId}/download`,
  );

export type RequestAssetUploadInput = z.infer<
  (typeof RequestAssetUpload)['http']['request']['body']
>;

export const requestAssetUpload = async ({
  workspaceId,
  assetId,
  input,
}: { assetId: string; input: RequestAssetUploadInput } & WithWorkspaceId) =>
  integrationAssetStorageApi.post<UploadRequestResponse>(
    `/workspaces/${workspaceId}/assets/${assetId}/upload`,
    input,
  );
